/* eslint-disable no-underscore-dangle */

import React, {
	useCallback,
	useContext,
	useEffect,
	useRef
} from 'react';
import classNames from 'classnames';

import { formatPrice } from '../../utils';
import renderIcon from '../../utils/render-icon';

import Modal from '../Modal';
import Button from '../Button';
import LineItem from '../LineItem';

import { StoreContext } from '../../context/store-context';
import { AuthContext } from '../../context/auth-context';
import { Nordic } from '../../utils/clients';
import CartProductSlider from '../CartProductSlider';

const ns = `cart-flyout`;
const dialogId = 'cart-flyout-dialog';
const titleId = 'cart-flyout-title';

const CartFlyout = ({ location }) => {
	const {
		cart,
		loading,
		isFlyoutCartOpen,
		onFlyoutCartClose,
	} = useContext(StoreContext);
	const { state: authState } = useContext(AuthContext);
	const { idToken } = authState.auth;
	const titleRef = useRef(null);
	const removeButtonRefs = useRef({});
	const pendingFocusTarget = useRef(null);

	const emptyCart = cart.lineItems.length === 0;

	const rootClassnames = classNames({
		[`${ ns }`]: true,
	});

	const setRemoveButtonRef = useCallback((itemId, node) => {
		if (node) {
			removeButtonRefs.current[itemId] = node;
			return;
		}

		delete removeButtonRefs.current[itemId];
	}, []);

	const handleLineItemRemoveRequest = useCallback((itemId, shouldRestoreFocus = false) => {
		if (!shouldRestoreFocus) {
			pendingFocusTarget.current = null;
			return;
		}

		const lineItemIds = cart.lineItems.map(({ id }) => id);
		const itemIndex = lineItemIds.indexOf(itemId);

		pendingFocusTarget.current = {
			nextItemId: lineItemIds[itemIndex + 1] || null,
			previousItemId: itemIndex > 0 ? lineItemIds[itemIndex - 1] : null,
		};
	}, [cart.lineItems]);

	useEffect(() => {
		if (!pendingFocusTarget.current || loading) {
			return;
		}

		const {
			previousItemId,
			nextItemId,
		} = pendingFocusTarget.current;

		const focusTarget = (
			(previousItemId && removeButtonRefs.current[previousItemId]) ||
			(nextItemId && removeButtonRefs.current[nextItemId]) ||
			titleRef.current
		);

		if (focusTarget) {
			focusTarget.focus();
			pendingFocusTarget.current = null;
		}
	}, [cart.lineItems, loading]);

	const calculateSubtotal = () => {
		let subtotal = 0;
		let currency;

		cart.lineItems.forEach(item => {
			const customAttrs = {};
			const {
				quantity,
				variant: {
					price: {
						amount: price,
						currencyCode,
					},
				},
			} = item;

			const numericPrice = Number(price);

			if (!currency) {
				currency = currencyCode;
			}

			item.customAttributes.forEach(({ key, value }) => {
				customAttrs[key] = value;
			});

			if (customAttrs?._sellingPlanId && customAttrs?._sellingPlanGroup) {
				const spg = JSON.parse(customAttrs._sellingPlanGroup);

				const {
					discount_amount: discountAmount,
				} = spg.include.product;

				const discount = discountAmount / 100;

				subtotal += ((numericPrice - (numericPrice * discount)) * quantity);
			} else {
				subtotal += numericPrice * quantity;
			}
		});

		return formatPrice(currency, subtotal);
	};

	// If no subscription items present, send user to Shopify
	// checkout, otherwise send to Shopify /cart page

	const getRedirectURL = async (checkoutURL) => {
		try {
			if (!idToken) {
				return checkoutURL;
			}

			const nordic = new Nordic(idToken);
			const { multipassURL } = await nordic.getMultipassURL(checkoutURL);
			return multipassURL || checkoutURL;
		} catch (error) {
			console.error(error.json || error);
			return checkoutURL;
		}
	};

	const handleCheckout = async () => {
		const checkoutUrl = new URL(cart.webUrl);

		//	Append UTM parameters to checkout url for tracking purposes.
		const utmParameterKeysInLocalStorage = Object.keys(window.localStorage).filter(key => /utm/i.test(key));
		utmParameterKeysInLocalStorage.forEach(key => {
			const value = window.localStorage.getItem(key);
			if (!value) return;
			checkoutUrl.searchParams.set(key, value);
		});

		const redirectURL = await getRedirectURL(checkoutUrl);
		window.open(redirectURL, '_self');
	};

	return (
		<Modal
			modifier={rootClassnames}
			onRequestClose={() => { onFlyoutCartClose(); }}
			isOpen={isFlyoutCartOpen}
			id={dialogId}
			role="dialog"
			contentLabel="Your Bag"
			aria={{
				labelledby: titleId,
				modal: true,
			}}
			shouldFocusAfterRender
			shouldReturnFocusAfterClose
			onAfterOpen={() => {
				titleRef.current?.focus();
			}}
			shouldHideCloseButton
		>
			<div className={`${ ns }__header`}>
				<h4 id={titleId} ref={titleRef} tabIndex={-1}>Your Bag</h4>
				<button
					type="button"
					className="site-modal__close"
					onClick={onFlyoutCartClose}
				>
					{renderIcon('x')}
					<span className="visually-hidden">Close shopping bag</span>
				</button>
			</div>
			<div className={`${ ns }__body`}>
				{emptyCart
					? (
						<div className={`${ ns }__empty`}>
							<p>You have no items in your bag.</p>
						</div>
					) : (
						<ul className={`${ ns }__line-items`}>
							{cart.lineItems.map((item, index) => {
								return (
									<LineItem
										item={item}
										key={item.id}
										location={location}
										index={index}
										onRemoveRequest={handleLineItemRemoveRequest}
										removeButtonRef={node => {
											setRemoveButtonRef(item.id, node);
										}}
									/>
								);
							})}
						</ul>
					)}
			</div>
			<div className={`${ ns }__footer`}>
				<CartProductSlider
					onFlyoutCartClose={onFlyoutCartClose}
					location={location}
				/>
				{!emptyCart && (
					<>
						<div className={`${ ns }__totals`}>
							<div className={`${ ns }__totals-total`}>
								<p>Subtotal</p>
								<p>{calculateSubtotal()}</p>
							</div>
							<div className={`${ ns }__totals-message`}>
								<p />
								<p>Tax, shipping, and discounts calculated at checkout</p>
							</div>
						</div>
						<div className={`${ ns }__checkout`}>
							<Button
								onClick={handleCheckout}
								disabled={loading}
							>
								{loading ? 'Updating…' : 'Checkout'}
							</Button>
						</div>
					</>
				)}
			</div>
		</Modal>
	);
};

export default CartFlyout;
